import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Users, Building2, Plus, Loader2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { kontakteAPI, type KontaktTag, type KontaktCreateData, type KontaktSearchResult } from '@/utils/api'

interface CreateKontaktModalProps {
  tags: KontaktTag[]
  onCreated?: () => void
  triggerButton?: React.ReactNode
  /** Inline mode: don't navigate, return created kontakt via callback */
  mode?: 'standalone' | 'inline'
  /** Callback with created kontakt data (for inline mode) */
  onCreatedInline?: (kontakt: KontaktSearchResult) => void
  /** Control open state externally */
  externalOpen?: boolean
  /** Callback when open state changes (for controlled mode) */
  onOpenChange?: (open: boolean) => void
}

type Step = 'type' | 'details'

export function CreateKontaktModal({
  tags,
  onCreated,
  triggerButton,
  mode = 'standalone',
  onCreatedInline,
  externalOpen,
  onOpenChange,
}: CreateKontaktModalProps) {
  const navigate = useNavigate()
  const [internalOpen, setInternalOpen] = useState(false)

  // Use external control if provided, otherwise use internal state
  const isControlled = externalOpen !== undefined
  const open = isControlled ? externalOpen : internalOpen
  const [step, setStep] = useState<Step>('type')
  const [isSaving, setIsSaving] = useState(false)

  const [formData, setFormData] = useState<KontaktCreateData>({
    kontakt_type: 'person',
    anrede: '',
    first_name: '',
    last_name: '',
    company_name: '',
    legal_form: '',
    iata_code: '',
    email: '',
    phone: '',
    tag_ids: [],
  })

  const [selectedTags, setSelectedTags] = useState<KontaktTag[]>([])

  const resetForm = () => {
    setStep('type')
    setFormData({
      kontakt_type: 'person',
      anrede: '',
      first_name: '',
      last_name: '',
      company_name: '',
      legal_form: '',
      iata_code: '',
      email: '',
      phone: '',
      tag_ids: [],
    })
    setSelectedTags([])
  }

  const setOpen = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen)
    } else {
      setInternalOpen(newOpen)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      resetForm()
    }
  }

  const selectType = (type: 'person' | 'company') => {
    setFormData({ ...formData, kontakt_type: type })
    setStep('details')
  }

  const toggleTag = (tag: KontaktTag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id)
    if (isSelected) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
  }

  const handleSave = async () => {
    // Validate required fields
    if (formData.kontakt_type === 'person') {
      if (!formData.first_name || !formData.last_name) {
        toast.error('Bitte Vor- und Nachname angeben')
        return
      }
    } else {
      if (!formData.company_name) {
        toast.error('Bitte Firmennamen angeben')
        return
      }
    }

    setIsSaving(true)
    try {
      const createData: KontaktCreateData = {
        ...formData,
        tag_ids: selectedTags.map(t => t.id),
      }

      const created = await kontakteAPI.createKontakt(createData)
      toast.success('Kontakt erfolgreich erstellt')
      handleOpenChange(false)
      onCreated?.()

      if (mode === 'inline' && onCreatedInline) {
        // In inline mode, convert to KontaktSearchResult format and return via callback
        const searchResult: KontaktSearchResult = {
          id: created.id,
          uuid: created.uuid,
          kontakt_type: created.kontakt_type,
          display_name: created.display_name,
          formal_name: created.formal_name,
          iata_code: created.iata_code,
          primary_email: created.primary_email,
          tags: created.tags.map(t => t.name),
        }
        onCreatedInline(searchResult)
      } else {
        // In standalone mode, navigate to the new contact
        navigate({ to: '/lawyer/kontakte/$kontaktId', params: { kontaktId: String(created.id) } })
      }
    } catch (err) {
      console.error('Failed to create kontakt:', err)
      toast.error('Kontakt konnte nicht erstellt werden')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Neuer Kontakt
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Neuen Kontakt erstellen</DialogTitle>
          <DialogDescription>
            {step === 'type'
              ? 'Wählen Sie den Kontakttyp'
              : formData.kontakt_type === 'person'
              ? 'Geben Sie die Personendaten ein'
              : 'Geben Sie die Unternehmensdaten ein'}
          </DialogDescription>
        </DialogHeader>

        {step === 'type' ? (
          // Step 1: Type Selection
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={() => selectType('person')}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-900">Person</p>
                <p className="text-sm text-slate-500">Natürliche Person</p>
              </div>
            </button>

            <button
              onClick={() => selectType('company')}
              className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-colors"
            >
              <div className="p-3 rounded-full bg-purple-100">
                <Building2 className="h-8 w-8 text-purple-600" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-900">Unternehmen</p>
                <p className="text-sm text-slate-500">Firma / Organisation</p>
              </div>
            </button>
          </div>
        ) : (
          // Step 2: Details Form
          <div className="space-y-4 py-4">
            {formData.kontakt_type === 'person' ? (
              // Person fields
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Anrede</Label>
                    <Select
                      value={formData.anrede || 'none'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, anrede: value === 'none' ? '' : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="herr">Herr</SelectItem>
                        <SelectItem value="frau">Frau</SelectItem>
                        <SelectItem value="none">Keine Anrede</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vorname *</Label>
                    <Input
                      value={formData.first_name || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, first_name: e.target.value })
                      }
                      placeholder="Max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nachname *</Label>
                    <Input
                      value={formData.last_name || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, last_name: e.target.value })
                      }
                      placeholder="Mustermann"
                    />
                  </div>
                </div>
              </>
            ) : (
              // Company fields
              <>
                <div className="space-y-2">
                  <Label>Firmenname *</Label>
                  <Input
                    value={formData.company_name || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value })
                    }
                    placeholder="Muster GmbH"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rechtsform</Label>
                    <Input
                      value={formData.legal_form || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, legal_form: e.target.value })
                      }
                      placeholder="GmbH, AG, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IATA-Code</Label>
                    <Input
                      value={formData.iata_code || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          iata_code: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="LH"
                      maxLength={3}
                      className="font-mono uppercase"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Quick contact info */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">
                Kontaktdaten (optional)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="email@beispiel.de"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            </div>

            {/* Tags selection */}
            {tags.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const isSelected = selectedTags.some(t => t.id === tag.id)
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                          isSelected
                            ? 'bg-slate-900 border-slate-900 text-white'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3 inline mr-1" />}
                        {tag.display_name}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setStep('type')}
                disabled={isSaving}
              >
                Zurück
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Erstellen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
